import React from "react";
import Slider from "react-slick";
import styled, { css } from "styled-components";
import { pageGutterSpace } from "../../../settings";
import breakpoint from "../../../utils/breakpoint";
import getBorderColor from "../../../utils/getBorderColor";
import getBorderRadius from "../../../utils/getBorderRadius";
import getBoxShadow from "../../../utils/getBoxShadow";
import getColor from "../../../utils/getColor";
import getIconColor from "../../../utils/getIconColor";
import space, { spaceFactor } from "../../../utils/space";
import transition from "../../../utils/transition";
import unit from "../../../utils/unit";
var ArrowSvg = function ArrowSvg(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    d: "M12.96.848a1.5 1.5 0 0 1 .192 2.113L3.953 14l9.2 11.04a1.5 1.5 0 0 1-2.305 1.921l-10-12a1.5 1.5 0 0 1 0-1.921l10-12A1.5 1.5 0 0 1 12.96.848z"
  }));
};
ArrowSvg.defaultProps = {
  xmlns: "http://www.w3.org/2000/svg",
  width: "14",
  height: "28"
};
var ratios = {
  "default": 3 / 2,
  widescreen: 16 / 9
};
var textMarginTopSpace = 5;
var dotsHeight = 2; /* height of dot "line" in pixels */
var dotsHeightSpace = 2; /* height of clickable space = final dot's height */
var dotsMarginTopSpace = 5;
var arrowWidth = 14;
var arrowGutterSpaceOnMedium = 4;
var arrowGutterSpaceOnLarge = 5;
var arrowAreaWidthOnMedium = arrowWidth + arrowGutterSpaceOnMedium * spaceFactor;
var arrowAreaWidthOnLarge = arrowWidth + arrowGutterSpaceOnLarge * spaceFactor;
var slidesLayoutStyles = function slidesLayoutStyles(screen) {
  var asideSlidesScale = 0.85;
  var translateValue;
  if (screen === "small") {
    translateValue = pageGutterSpace * spaceFactor;
  } else if (screen === "medium") {
    translateValue = (pageGutterSpace + arrowGutterSpaceOnMedium) * spaceFactor + arrowWidth;
  } else {
    translateValue = 2 * arrowGutterSpaceOnLarge * spaceFactor + arrowWidth;
  }
  return css(["transform:translateX(", ") scale(", ");&.slick-center{transform:scale(1);+ *{transform:translateX(", ") scale(", ");}}"], unit(-1 * translateValue), asideSlidesScale, unit(translateValue), asideSlidesScale);
};
export var Wrapper = styled.div.withConfig({
  componentId: "sc-vk2qfa-0"
})(["position:relative;", "{width:calc(100% - ", ");margin:0 auto;}", "{width:", "%;}"], breakpoint("medium"), unit(2 * arrowAreaWidthOnMedium), breakpoint("large"), 5 / 8 * 100);
export var MediaContainer = styled.div.withConfig({
  componentId: "sc-vk2qfa-1"
})(function (_ref) {
  var $ratio = _ref.$ratio,
    theme = _ref.theme;
  return css(["overflow:hidden;border-radius:", ";box-shadow:", ";img{width:100%;pointer-events:none;}", ""], getBorderRadius(theme, "large"), getBoxShadow(theme, "small"), $ratio !== "original" && css(["position:relative;padding-bottom:", "%;img{position:absolute;height:100%;object-fit:cover;}"], 100 / ratios[$ratio]));
});
export var TextContainer = styled.div.withConfig({
  componentId: "sc-vk2qfa-2"
})(function (_ref2) {
  var $hasArrowsOnMobile = _ref2.$hasArrowsOnMobile;
  return css(["display:grid;grid-gap:", ";justify-items:center;margin-top:", ";text-align:center;", "{padding:0 ", ";}", ""], space(1), space(textMarginTopSpace), breakpoint("medium"), space(3), $hasArrowsOnMobile && css(["padding:0 ", ";"], space(3)));
});
export var StyledCarousel = styled(Slider).withConfig({
  componentId: "sc-vk2qfa-3"
})(function (_ref3) {
  var $onColor = _ref3.$onColor,
    theme = _ref3.theme;
  return css(["padding-bottom:", ";.slick-list{overflow:visible;}.slick-slide{", ";", ";", "{", ";}", "{", ";}&:not(.slick-center){", "{visibility:hidden;}}}.slick-dots{display:grid !important;grid-gap:", ";grid-template-columns:repeat(auto-fit,minmax(0,1fr));margin-top:", ";button{position:relative;width:100%;height:", ";border:0;background:", ";font-size:0;cursor:pointer;&:hover{background:", ";}&::after{content:\"\";position:absolute;left:0;width:100%;height:", ";transform:translateY(calc(-50% + ", "));}}.slick-active button{background:", ";cursor:default;}}"], unit(dotsHeight), transition("transform"), slidesLayoutStyles("small"), breakpoint("medium"), slidesLayoutStyles("medium"), breakpoint("large"), slidesLayoutStyles("medium"), TextContainer, space(1), space(dotsMarginTopSpace), unit(dotsHeight), getBorderColor(theme, $onColor, "medium"), getBorderColor(theme, $onColor, "medium", "hover"), space(dotsHeightSpace), unit(dotsHeight / 2), getColor(theme, "primary"));
});

/*
 * 1. Places arrows in right place by formula:
 *    top = (H + G - D) / 2, where:
 *      H = height of whole container (100%)
 *      G = gutter between slide image and text
 *      D = sum of height and gutter of dots
 * 2. Scales up clickable space of arrows to their original size
 * */
export var ArrowsContainer = styled.div.withConfig({
  componentId: "sc-vk2qfa-4"
})(function (_ref4) {
  var $ratio = _ref4.$ratio,
    $biggestImageHeight = _ref4.$biggestImageHeight,
    $hasArrowsOnMobile = _ref4.$hasArrowsOnMobile;
  var mobileArrowsScale = 0.6;
  return css(["position:absolute;top:0;right:0;left:0;pointer-events:none;", " ", "{margin-right:", ";margin-left:", ";}", "{margin-right:", ";margin-left:", ";}", ""], $hasArrowsOnMobile ? css(["top:calc( ( 100% + ", " - ", " ) / 2 );svg{transform:scale(", ");&::after{transform:scale(", ");}}"], space(textMarginTopSpace), space(dotsHeightSpace + dotsMarginTopSpace), mobileArrowsScale, 1 / mobileArrowsScale) : css(["", "{display:none;}"], breakpoint("small", "medium")), breakpoint("medium"), unit(-1 * arrowAreaWidthOnMedium), unit(-1 * arrowAreaWidthOnMedium), breakpoint("large"), unit(-1 * arrowAreaWidthOnLarge), unit(-1 * arrowAreaWidthOnLarge), $ratio !== "original" ? css(["padding-bottom:", "%;"], 100 / ratios[$ratio]) : css(["height:", ";"], unit($biggestImageHeight)));
});
export var ArrowButton = styled.button.withConfig({
  componentId: "sc-vk2qfa-5"
})(function (_ref5) {
  var $direction = _ref5.$direction,
    $onColor = _ref5.$onColor,
    theme = _ref5.theme;
  var clickableSizeSpace = 5;
  return css(["position:absolute;top:50%;right:", ";padding:0;transform:translateY(-50%) ", ";border:0;background:none;cursor:pointer;pointer-events:all;&:hover *{fill:", ";}&::after{content:\"\";position:absolute;top:calc(50% - ", ");left:calc(50% - ", ");width:", ";height:", ";}"], $direction === "right" && 0, $direction === "right" && "rotate(180deg)", getIconColor(theme, $onColor, "high", "hover"), space(clickableSizeSpace / 2), space(clickableSizeSpace / 2), space(clickableSizeSpace), space(clickableSizeSpace));
});
export var ArrowIcon = styled(ArrowSvg).withConfig({
  componentId: "sc-vk2qfa-6"
})(function (_ref6) {
  var $onColor = _ref6.$onColor,
    theme = _ref6.theme;
  return css(["display:block;fill:", ";"], getIconColor(theme, $onColor, "high"));
});